import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    clearTimeout(this.#zichtbaarheidTimer);

    try {
      const { dialog } = this.refs;
      if (dialog?.open) {
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
        dialog.close();
      }
    } catch (e) {}
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #zichtbaarheidTimer;

  #handleCartAdd = () => {
    if (!this.hasAttribute('auto-open')) return;

    this.showDialog();

    // Vangnet tegen een "open maar onzichtbare" lade (die bevriest de pagina):
    // staat hij na het inschuiven nog steeds buiten beeld, sluit hem dan netjes.
    // Pas controleren NA de animatie. De lade schuift van buiten het scherm naar
    // binnen, dus direct na het openen staat hij altijd nog buiten beeld; de
    // controle zat eerst twee frames na het openen en klapte de lade daardoor
    // meteen weer dicht.
    clearTimeout(this.#zichtbaarheidTimer);
    this.#zichtbaarheidTimer = setTimeout(() => {
      const { dialog } = this.refs;
      if (!dialog?.open || dialog.classList.contains('dialog-closing')) return;

      const rect = dialog.getBoundingClientRect();
      if (rect.width === 0 || rect.left >= window.innerWidth) {
        this.closeDialog();
      }
    }, 1000);
  };

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
